<template>
  <div
    class="organization-chart-node"
    :class="nodeClasses"
  >
    <div
      v-if="isImageVisible"
      class="organization-chart-node__thumbnail"
      :style="{ backgroundImage: `url(${image})` }"
    >
      <PhotoIcon
        v-if="!image"
        aria-hidden="true"
      />
    </div>
    <div
      class="organization-chart-node__info"
      :class="infoClasses"
    >
      <div
        ref="nameRef"
        v-tooltip.permanent.top="tooltip"
        class="organization-chart-node__name asset-node"
        :class="nameClasses"
        @click.stop="handleClickName"
        @dblclick.stop
      >
        <component :is="name" />
      </div>
      <div
        v-if="isTickerVisible"
        class="organization-chart-node__ticker asset-node"
      >
        <component :is="ticker" />
      </div>
      <div
        v-if="balance"
        class="organization-chart-node__balance number-node"
      >
        {{ balance }}
      </div>
      <div
        v-if="amount"
        class="organization-chart-node__amount number-node"
      >
        {{ amount }}
      </div>
      <div 
        v-if="children?.length"
        class="organization-chart-node__btn-toggle"
        @click="toggleChildren"
        @touchend="toggleChildren"
      >
        <component
          :is="childrenExpanded ? MinusIcon : PlusIcon"
          aria-hidden="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h, inject, Ref, ref, useTemplateRef, watch } from 'vue'
import { useResizeObserver } from '@vueuse/core'

import { OrganizationChartNode } from '../utils/types'
import { AnalyticsField } from '@types'

import { handleCatchedError } from '@/helpers/common'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useDocumentsStore } from '@/store/documents'
import { useRepositoriesStore } from '@/store/repositories'

import { MinusIcon, PhotoIcon, PlusIcon } from '@heroicons/vue/24/outline'

type Props = {
  item: OrganizationChartNode
  children?: string[]
  childrenExpanded?: boolean
  toggleChildren?: () => void
  visibleFields?: string[]
  valueFormatter?: (value: number) => string
  amountFormatter?: (value: number) => string
}

const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()
const documentsStore = useDocumentsStore()
const repositoryStore = useRepositoriesStore()

const image = ref<string>()
const isNameEllipsis = ref(false)

const search = inject<Ref<string>>('search')

const nameRef = useTemplateRef('nameRef')

useResizeObserver(nameRef, () => {
  if (!nameRef.value) return
  isNameEllipsis.value = nameRef.value.offsetWidth < nameRef.value.scrollWidth
})

const name = computed(() => {
  if (isRepoLevel.value) return h('span', repositoryStore.getCurrentName)
  const value = props.item.asset_name
  return markSearchedValue(value, search?.value)
})

const tooltip = computed(() => {
  if (!isNameEllipsis.value) return
  return {
    content: isRepoLevel.value ? repositoryStore.getCurrentName : props.item.asset_name,
    handleResize: true,
  }
})

const ticker = computed(() => {
  const value = props.item.asset_ticker
  if (value === props.item.asset_name) return h('span', '---')
  return markSearchedValue(value, search?.value)
})

const isTickerVisible = computed(() => props.visibleFields?.includes('ticker') && !isRepoLevel.value)

const balance = computed(() => props.visibleFields?.includes('balance') && (props.valueFormatter ? props.valueFormatter(props.item.balance) : `${props.item.balance}`))
const amount = computed(() => props.visibleFields?.includes('amount') && (!isRepoLevel.value && props.item.amount) && (props.amountFormatter ? props.amountFormatter(props.item.amount) : `${props.item.amount}`))

const isRepoLevel = computed(() => props.item.id === AnalyticsField.REPO_ROOT)

const isImageVisible = computed(() => props.visibleFields?.includes('thumbnail') && !isRepoLevel.value)

const nodeClasses = computed(() => ({
  'py-4! bg-body-gray': isRepoLevel.value,
  'isParentNode': !!props.children?.length,
}))

const infoClasses = computed(() => ({
  'organization-chart-node__info--centered': !isImageVisible.value,
}))

const nameClasses = computed(() => ({
  'ui-link': !isRepoLevel.value,
  'organization-chart-node__name--root': isRepoLevel.value,
}))

const instance = computed(() => assetsBunchStore.getElementById(props.item.asset_id))

const handleClickName = () => {
  instance.value?.openDrawer()
}

const markSearchedValue = (value: string, search?: string) => {
  if (!search) return h('span', value)
  const start = value.toLowerCase().indexOf(search.toLowerCase())
  let end
  if (start < 0) {
    return h('span', value)
  } else {
    end = start + search.length
  }
  const part1 = value.slice(0, start)
  const part2 = value.slice(start, end)
  const part3 = value.slice(end)
  return h('span', [part1, h('mark', part2), part3])
}

watch(() => props.visibleFields?.includes('thumbnail') && props.item.asset_thumbnail, async value => {
  if (!value) return
  if (instance.value?.thumbnailUrl) {
    image.value = instance.value.thumbnailUrl
    return
  }
  try {
    const document = await documentsStore.fetchSingle(value)
    const thumbnail = document.url
    if (!thumbnail) return
    instance.value && (instance.value.thumbnailUrl = thumbnail)
    image.value = thumbnail
  } catch (e) {
    handleCatchedError(e as string)
  }
}, { immediate: true })
</script>

<style>
:root {
  --vue3-org-chart-line-bottom: 1rem;
  --vue3-org-chart-line-color: #d1d5db;
}

:root[class~='dark'] {
  --vue3-org-chart-line-color: #4b5563;
}

.organization-chart-node {
  @apply w-48;
  @apply relative;
  @apply flex items-start gap-2;
  @apply p-2 pb-3;
  @apply border border-indigo-200 dark:border-indigo-300;
  @apply rounded-md;

  &__thumbnail {
    @apply size-10 shrink-0;
    @apply flex items-center justify-center;
    @apply rounded-full;
    @apply border border-common-border;
    @apply bg-body-gray bg-center bg-cover bg-no-repeat;

    svg {
      @apply size-5;
      @apply text-common-border;
    }
  }

  &__info {
    @apply flex flex-col gap-1;
    @apply overflow-hidden;

    &--centered {
      @apply flex-auto;
      @apply text-center;
    }
  }

  &__name {
    @apply w-full;
    @apply text-base sm:text-sm;
    @apply leading-5 sm:leading-tight;
    @apply truncate;
    @apply text-gray-500! dark:text-gray-400!;

    &--root {
      @apply text-base leading-tight;
    }

    &.ui-link {
      @apply cursor-pointer;
      @apply decoration-gray-300 dark:decoration-gray-600;
      @apply underline-offset-2;
    }
  }

  &__ticker {
    @apply text-sm sm:text-xs;
    @apply text-gray-400 dark:text-gray-500;
    @apply font-light;
    @apply truncate;
  }

  &__balance {
    @apply text-base sm:text-xs;
    @apply text-gray-700 dark:text-gray-300;
    @apply leading-none;
  }

  &__amount {
    @apply text-gray-400 dark:text-gray-500;
    @apply font-light;
    @apply text-xs;
  }

  &__btn-toggle {
    @apply size-5;
    @apply absolute top-full left-1/2 -translate-x-1/2 -translate-y-1/2;
    @apply flex justify-center items-center;
    @apply bg-gray-100 hover:bg-gray-200;
    @apply dark:bg-gray-800 dark:hover:bg-gray-900;
    @apply text-indigo-700 dark:text-indigo-300;
    @apply rounded-full;
    @apply z-50;
    @apply cursor-pointer;

    svg {
      @apply size-3;
    }
  }
}
</style>
